/**
 * Global store (Zustand).
 * Holds: current member, current family, theme, skin, peek state, active workspace.
 * Per v8 §18, mirrors can use Zustand; home-studio modules can use either.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type Role = 'adult' | 'child'
export type Subtype = 'primary' | 'co_manager' | null

export interface Member {
  id: string
  name: string
  avatar_url: string | null
  role: Role
  subtype: Subtype
  ep_opt_out: boolean
}

export type SkinId = 'garden' | 'journey' | 'light'

export type WorkspaceId = 'tasks' | 'calendar' | 'chat' | 'resources' | 'history' | 'achievements'

export type ChatChannel = 'family' | 'adults' | 'dm'

interface AppState {
  // Auth
  session: { userId: string } | null
  currentMember: Member | null
  setSession: (session: { userId: string } | null) => void
  setCurrentMember: (m: Member | null) => void

  // Dev mode — bypass auth + use local seed data. No Supabase calls.
  // Persisted so reloads stay in dev mode until the operator turns it off.
  dev: boolean
  setDev: (dev: boolean) => void

  // Theme
  skin: SkinId
  setSkin: (skin: SkinId) => void

  // Workspace
  activeWorkspace: WorkspaceId
  setActiveWorkspace: (id: WorkspaceId) => void

  // Peek
  peek: { kind: 'task' | 'resource' | 'member' | 'chat' | null; id: string | null }
  openPeek: (peek: { kind: 'task' | 'resource' | 'member' | 'chat'; id: string }) => void
  closePeek: () => void

  // Chat
  chatChannel: ChatChannel
  chatDmWith: string | null
  setChatChannel: (channel: ChatChannel, dmWith?: string | null) => void
}

export const useApp = create<AppState>()(
  persist(
    (set) => ({
      session: null,
      currentMember: null,
      setSession: (session) => set({ session }),
      setCurrentMember: (currentMember) => set({ currentMember }),

      dev: false,
      setDev: (dev) => set({ dev }),

      skin: 'garden',
      setSkin: (skin) => {
        set({ skin })
        document.documentElement.dataset.skin = skin
      },

      activeWorkspace: 'tasks',
      setActiveWorkspace: (activeWorkspace) => set({ activeWorkspace }),

      peek: { kind: null, id: null },
      openPeek: (peek) => set({ peek: { ...peek } }),
      closePeek: () => set({ peek: { kind: null, id: null } }),

      chatChannel: 'family',
      chatDmWith: null,
      setChatChannel: (chatChannel, chatDmWith = null) => set({ chatChannel, chatDmWith }),
    }),
    {
      name: 'home-studio-app',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        skin: state.skin,
        activeWorkspace: state.activeWorkspace,
        chatChannel: state.chatChannel,
        dev: state.dev,
        currentMember: state.currentMember,
      }),
    }
  )
)
